import firebaseService from './FirebaseService.js'

/**
 * FileService - Handles file uploads with automatic Firebase to Cloudflare migration for videos
 * 
 * Video Upload Process:
 * 1. Upload video to Firebase Storage
 * 2. Upload video to Cloudflare R2
 * 3. Wait for Firebase to generate thumbnails
 * 4. Generate thumbnail URLs
 * 5. Delete video from Firebase ONLY (keeps video in Cloudflare for delivery)
 * 6. Return Cloudflare URL and thumbnail URLs
 * 
 * Error Handling:
 * - If Cloudflare upload fails, Firebase video is deleted to avoid storage costs
 * - User must retry the upload (no fallback to Firebase video)
 * 
 * Benefits:
 * - Reduced Firebase storage costs (videos deleted from Firebase after upload)
 * - Faster video delivery via Cloudflare CDN (videos stay in Cloudflare)
 * - No accumulation of failed uploads in Firebase storage
 * - Thumbnails remain in Firebase for quick access
 * 
 * @author MyDigiSign Team
 */
class FileService {
  /**
   * Upload a file with progress callback
   */
  async uploadFile(fileName, content, progressCallback) {
    try {
      // If content is already a URL, return it with size conversion
      if (content.indexOf("http") === 0) {
        if (progressCallback) {
          progressCallback(fileName);
        }
        return content.replace("_640x360.", "_1920x1080.").replace("_640x640.", "_1920x1920.");
      }
      
      // If it's a video file (.mp4), upload to both Firebase and Cloudflare
      if (fileName.indexOf('.mp4') !== -1) {
        // Upload to Firebase first
        const firebaseUrl = await firebaseService.uploadFile(fileName, content, progressCallback);
        
                 try {
           // Upload to Cloudflare using direct R2 upload
           const { default: cloudflareService } = await import('./CloudflareService.js');
           await cloudflareService.uploadFileDirect(content, this.getFileName(firebaseUrl));
          
          // Wait for video thumbnails to be created before deleting the video
          await firebaseService.waitForVideoImageFilesCreated(fileName);
          
          // Generate thumbnail URLs before deleting the video
          const thumbUrl = await firebaseService.getThumbUrl(firebaseUrl, false);
          const thumbUniversalUrl = await firebaseService.getThumbUrl(firebaseUrl, true);
          
          // Delete from Firebase after successful Cloudflare upload and thumbnail creation
          await firebaseService.deleteFile(fileName, false); // false = don't delete from Cloudflare
          
          // Return the Cloudflare URL and thumbnail URLs
          const cloudflareUrl = await cloudflareService.getPublicFileUrl(this.getFileName(firebaseUrl));
          return {
            videoUrl: cloudflareUrl,
            thumbUrl: thumbUrl,
            thumbUniversalUrl: thumbUniversalUrl
          };
          
        } catch (cloudflareError) {
          console.error(`Failed to upload to Cloudflare or process video: ${cloudflareError.message}`);
          
          // Option 1: Delete Firebase video to avoid storage costs and throw error
          // This forces the user to retry the upload
          try {
            await firebaseService.deleteFile(fileName, false); // false = don't delete from Cloudflare
            console.log(`Deleted Firebase video ${fileName} due to Cloudflare upload failure`);
          } catch (deleteError) {
            console.error(`Failed to delete Firebase video ${fileName}:`, deleteError);
          }
          
          // Re-throw the original Cloudflare error to indicate upload failure
          throw new Error(`Video upload failed: ${cloudflareError.message}. Please try again.`);
          
          // Option 2: Keep Firebase video as fallback (commented out)
          // Uncomment the lines below if you prefer to keep Firebase video as fallback
          /*
          const thumbUrl = await firebaseService.getThumbUrl(firebaseUrl, false);
          const thumbUniversalUrl = await firebaseService.getThumbUrl(firebaseUrl, true);
          
          return {
            videoUrl: firebaseUrl,
            thumbUrl: thumbUrl,
            thumbUniversalUrl: thumbUniversalUrl
          };
          */
        }
      }
      
      // For image files, resize on client and upload to Cloudflare
      const isImage = fileName.indexOf('.jpeg') !== -1 || 
                     fileName.indexOf('.jpg') !== -1 || 
                     fileName.indexOf('.png') !== -1;
      
      if (isImage) {
        console.log(`📸 Processing image with client-side resizing: ${fileName}`);
        
        try {
          // Convert base64 to blob
          const originalBlob = await this.base64ToBlob(content);
          
          // Resize to both 1920x1920 and 640x640 on client-side
          const highResBlob = await this.resizeImage(originalBlob, 1920, 1920);
          const thumbBlob = await this.resizeImage(originalBlob, 640, 640);
          
          console.log(`✅ Resized images: High-res ${highResBlob.size} bytes, Thumb ${thumbBlob.size} bytes`);
          
          // Upload both blobs directly to Cloudflare
          const { default: cloudflareService } = await import('./CloudflareService.js');
          
          // Get base filename and normalize to .jpeg
          const baseFileName = fileName.replace(/\.(jpeg|jpg|png)$/i, '');
          const highResFileName = `${baseFileName}_1920x1920.jpeg`;
          const thumbFileName = `${baseFileName}_640x640.jpeg`;
          
          console.log(`☁️ Uploading high-res to Cloudflare: ${highResFileName} (${highResBlob.size} bytes)`);
          await cloudflareService.uploadFile(highResFileName, highResBlob);
          console.log(`✅ Uploaded high-res to Cloudflare: ${highResFileName}`);
          
          console.log(`☁️ Uploading thumbnail to Cloudflare: ${thumbFileName} (${thumbBlob.size} bytes)`);
          await cloudflareService.uploadFile(thumbFileName, thumbBlob);
          console.log(`✅ Uploaded thumbnail to Cloudflare: ${thumbFileName}`);
          
          // Get both Cloudflare URLs
          const cloudflareUrl = await cloudflareService.getPublicFileUrl(highResFileName);
          const thumbnailUrl = await cloudflareService.getPublicFileUrl(thumbFileName);
          console.log(`✅ Images uploaded to Cloudflare:`);
          console.log(`   High-res: ${cloudflareUrl}`);
          console.log(`   Thumbnail: ${thumbnailUrl}`);
          
          // Return both URLs - both use the high-res 1920x1920 image
          return {
            imageUrl: cloudflareUrl,
            imageUniversalUrl: cloudflareUrl
          };
        } catch (cloudflareError) {
          console.error(`Failed to upload images to Cloudflare: ${cloudflareError.message}`);
          console.error(`Error stack:`, cloudflareError);
          // Fallback to Firebase
          console.log(`⚠️ Falling back to Firebase upload...`);
          const firebaseUrl = await firebaseService.uploadFile(fileName, content, progressCallback);
          return firebaseUrl;
        }
      }
      
      // For other files, just upload to Firebase
      return await firebaseService.uploadFile(fileName, content, progressCallback);
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Delete a file
   */
  async deleteFile(fileName) {
    try {
      const { default: cloudflareService } = await import('./CloudflareService.js');
      
      // If it's a video file (.mp4), delete from both Cloudflare and Firebase
      if (fileName.indexOf('.mp4') !== -1) {
        await cloudflareService.deleteFile(fileName);
        return await firebaseService.deleteFile(fileName);
      }
      
      // If it's an image file, delete both 1920x1920 and 640x640 versions from Cloudflare
      if (fileName.indexOf('.jpeg') !== -1 || fileName.indexOf('.jpg') !== -1 || fileName.indexOf('.png') !== -1) {
        // Extract base filename (remove resolution suffix if present)
        const baseFileName = fileName
          .replace(/_1920x1920\.jpeg$/i, '.jpeg')
          .replace(/_640x640\.jpeg$/i, '.jpeg')
          .replace(/\.(jpeg|jpg|png)$/i, '');
        
        // Delete both resolutions from Cloudflare
        const highResFileName = `${baseFileName}_1920x1920.jpeg`;
        const thumbFileName = `${baseFileName}_640x640.jpeg`;
        
        console.log(`🗑️ Deleting image files from Cloudflare: ${highResFileName}, ${thumbFileName}`);
        
        try {
          await cloudflareService.deleteFile(highResFileName);
          console.log(`✅ Deleted ${highResFileName} from Cloudflare`);
        } catch (error) {
          console.warn(`⚠️ Failed to delete ${highResFileName} from Cloudflare:`, error);
        }
        
        try {
          await cloudflareService.deleteFile(thumbFileName);
          console.log(`✅ Deleted ${thumbFileName} from Cloudflare`);
        } catch (error) {
          console.warn(`⚠️ Failed to delete ${thumbFileName} from Cloudflare:`, error);
        }
      }
      
      // For all files, delete from Firebase (handles thumbnails, etc.)
      return await firebaseService.deleteFile(fileName);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Delete multiple files with filter
   */
  async deleteFiles(fileNameFilter, folderName) {
    try {
      const { default: cloudflareService } = await import('./CloudflareService.js');
      await cloudflareService.deleteFiles(fileNameFilter);
      return await firebaseService.deleteFiles(fileNameFilter, folderName);
    } catch (error) {
      console.error('Error deleting files:', error);
      throw error;
    }
  }

  /**
   * Extract filename from URL
   */
  getFileName(url) {
    // %2F - slash added by firebase to indicate a folder, we remove it
    url = url.split('/').pop().split('?')[0];
    return url.indexOf('%2F') === -1 ? url : url.split('%2F')[1];
  }

  /**
   * Get video cloud URL
   */
  async getVideoCloudUrl(videoUrl) {
    try {
      const fileName = this.getFileName(videoUrl);
      return await cloudflareService.getPublicFileUrl(fileName);
    } catch (error) {
      console.error('Error getting video cloud URL:', error);
      throw error;
    }
  }

  /**
   * Convert base64 data URL to Blob
   */
  async base64ToBlob(base64DataUrl) {
    const response = await fetch(base64DataUrl);
    return await response.blob();
  }

  /**
   * Resize image to specified dimensions
   */
  async resizeImage(imageBlob, maxWidth, maxHeight) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const url = URL.createObjectURL(imageBlob);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height / width) * maxWidth;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width / height) * maxHeight;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            console.log(`✅ Resized image to ${width}x${height}: ${blob.size} bytes`);
            resolve(blob);
          } else {
            reject(new Error('Failed to resize image'));
          }
        }, 'image/jpeg', 0.85);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image for resizing'));
      };
      
      img.src = url;
    });
  }
}

// Create singleton instance
const fileService = new FileService();
export default fileService; 